import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export type DisplayLanguage = "en" | "zh";

type TranslationValue =
  string | ((values: Record<string, string | number>) => string);

const STORAGE_KEY = "pagebunnyDisplayLanguage";

const translations = {
  en: {
    "common.appName": "PageBunny",
    "common.loading": "Loading...",
    "common.tryAgain": "Try Again",
    "common.refresh": "Refresh",
    "common.delete": "Delete",
    "common.completed": "Completed",
    "common.sections": "Sections",
    "common.sectionCount": ({ count }) => `${count} section(s)`,
    "common.words": ({ count }) => `${count} words`,
    "common.min": ({ count }) => `${count} min`,
    "common.untitledReading": "Untitled reading",
    "common.userReading": "User reading",
    "common.article": "Article",
    "common.importedReading": "Imported reading",
    "common.language": "Display language",
    "common.english": "English",
    "common.chinese": "中文",

    "nav.library": "Library",
    "nav.savedVocab": "Saved Vocab",
    "nav.history": "History",
    "nav.settings": "Settings",
    "nav.openMenu": "Open navigation menu",
    "nav.mobileLabel": "Mobile navigation",
    "nav.welcome": ({ name }) => `Welcome, ${name}!`,
    "nav.logout": "Logout",

    "login.startReading": "Start reading",
    "login.seeHowItWorks": "See how it works",
    "login.howItWorksTitle": "How it works",
    "login.howItWorksBody":
      "Bring your own English text and turn it into reading practice at the right level.",
    "login.workflow.levelTitle": "Choose your level",
    "login.workflow.levelBody":
      "Select the CEFR reading level that feels right for you.",
    "login.workflow.addTitle": "Add something to read",
    "login.workflow.addBody":
      "Paste text or upload a TXT/EPUB file from your device.",
    "login.workflow.readTitle": "Read and learn vocabulary",
    "login.workflow.readBody":
      "Read the adapted version, look up words, and save useful vocabulary.",
    "login.workflow.pasteLabel": "Paste text",
    "login.workflow.definitionLabel": "Definition",
    "login.workflow.saveLabel": "Save word",
    "login.heroTitle": "Read English you actually care about",
    "login.heroBody":
      "Paste text or upload a TXT/EPUB file. PageBunny adapts it to your CEFR level, helps you look up words, and saves vocabulary for review.",
    "login.privacyNote": "Your imported readings stay private to your account.",
    "login.exploreLabel": "Explore PageBunny",
    "login.exploreTextSimplifier": "Text simplifier",
    "login.exploreEpubReader": "EPUB reader",
    "login.exploreChineseLearners": "For Chinese learners",
    "login.loginTitle": "Log in",
    "login.loginSubtitle": "Open your private reading Library.",
    "login.email": "Email Address",
    "login.emailPlaceholder": "Enter your email",
    "login.password": "Password",
    "login.passwordPlaceholder": "Enter your password",
    "login.loginButton": "Login",
    "login.loggingIn": "Logging in...",
    "login.orContinue": "Or continue with",
    "login.signingIn": "Signing in...",
    "login.google": "Continue with Google",
    "login.noAccount": "Don't have an account? ",
    "login.signupLink": "Sign up",
    "login.signupTitle": "Create account",
    "login.signupSubtitle": "Save readings, words, and progress.",
    "login.name": "Your Name",
    "login.namePlaceholder": "Enter your name",
    "login.createAccount": "Create Account",
    "login.creatingAccount": "Creating account...",
    "login.hasAccount": "Already have an account? ",
    "login.backToLogin": "Back to login",
    "login.continueToApp": "Continue to app",
    "login.forgotPassword": "Forgot password?",
    "login.forgotTitle": "Reset your password",
    "login.forgotSubtitle":
      "Enter your email and we'll send a secure reset link.",
    "login.sendResetLink": "Send reset link",
    "login.sendingResetLink": "Sending...",
    "login.resetRequested":
      "If an account exists for this email, we've sent a password-reset link. Check your inbox and spam folder. If nothing arrives, verify the address or create a new account.",
    "login.resetRequestError":
      "Could not send the reset email. Please try again.",
    "login.passwordUpdated":
      "Your password has been updated. Log in with your new password.",
    "login.errorEmailPassword": "Please enter both email and password",
    "login.errorAllFields": "Please fill in all fields",
    "login.errorPasswordLength": "Password must be at least 6 characters long",
    "login.errorUnexpected": "An unexpected error occurred",
    "login.errorGoogle": "Google login failed",
    "login.errorConfirmEmail":
      "Check your email to finish creating your account. If you already signed up with this email, use Login or reset your password.",

    "reset.loading": "Verifying your reset link...",
    "reset.title": "Choose a new password",
    "reset.subtitle": "Use at least 6 characters.",
    "reset.newPassword": "New password",
    "reset.confirmPassword": "Confirm new password",
    "reset.update": "Update password",
    "reset.updating": "Updating...",
    "reset.invalidTitle": "Reset link unavailable",
    "reset.invalidBody": "This password reset link is invalid or has expired.",
    "reset.requestAnother": "Request another reset link",
    "reset.googleTitle": "Password managed by Google",
    "reset.googleBody": "Use Google to manage this account's password.",
    "reset.passwordLength": "Password must be at least 6 characters long.",
    "reset.passwordMismatch": "Passwords do not match.",
    "reset.updateError": "Could not update your password. Please try again.",
    "reset.cleanupTitle": "Could not clear password recovery",
    "reset.cleanupBody":
      "We couldn't securely clear this reset session. Retry cleanup before requesting another link.",
    "reset.retryCleanup": "Retry secure cleanup",

    "library.title": "Library",
    "library.subtitle":
      "Private readings you import for CEFR-adjusted practice.",
    "library.loadError": "Failed to load your Library",
    "library.loading": "Loading Library...",
    "library.emptyTitle": "Start with something you want to read",
    "library.emptyBody":
      "Paste an article, essay, newsletter, class reading, or upload a TXT/EPUB file. PageBunny will adapt it to your CEFR level and help you save vocabulary.",
    "library.privateNote": "Imported readings are private to your account.",
    "library.uploadCta": "Upload TXT/EPUB",
    "library.pasteCta": "Paste Text",
    "library.hideSections": "Hide sections",
    "library.lastOpened": ({ date }) => `Last opened ${date}`,
    "library.notOpened": "Not opened",
    "library.sourcePasted": "Pasted text",
    "library.sourceTxt": "TXT upload",
    "library.sourceEpub": "EPUB upload",
    "library.sourceExtension": "Extension",
    "library.importReady": "Import ready",
    "library.simplifiedProgress": ({ count, total }) =>
      `${count}/${total} simplified`,
    "library.uploadTitle": "Upload TXT/EPUB",
    "library.uploadBody":
      "Add a plain text file or EPUB to your private Library.",
    "library.titleOptional": "Title (optional)",
    "library.dropFile": "Drop a TXT or EPUB file here",
    "library.chooseFileLine": "or choose a file from your device",
    "library.chooseFile": "Choose File",
    "library.uploadLimits": "TXT up to 1 MB or EPUB up to 10 MB.",
    "library.uploading": "Uploading...",
    "library.uploadFile": "Upload File",
    "library.pasteTitle": "Paste Text",
    "library.pasteBody":
      "Import text from an article, essay, newsletter, or class reading.",
    "library.titlePlaceholder": "Title",
    "library.sourceUrl": "Source URL (optional)",
    "library.pastePlaceholder": "Paste the text you want to read",
    "library.importing": "Importing...",
    "library.importPasted": "Import Pasted Text",
    "library.retryPaste": "Retry pasted text import",
    "library.retryUpload": "Retry file import",
    "library.checkImportAgain": "Check import again",
    "library.importStillProcessing": "This reading is still being processed.",
    "library.selectSameFile": "Select the same file to retry",
    "library.adjustFailure": "Failed to adjust this reading",
    "library.retrySimplification": "Retry simplification",
    "library.checkSimplificationAgain": "Check again",
    "library.stillSimplifying": "Still simplifying",
    "library.simplifying": "Simplifying...",
    "library.read": "Read",
    "library.simplifyRead": "Simplify & Read",
    "library.creatingAdjusted": "Creating adjusted version...",
    "library.deleteConfirm": ({ title }) => `Delete "${title}"?`,
    "library.backToLibrary": "Back to Library",
    "library.readingUnavailable": "This reading is unavailable.",
    "library.readerHeading": ({ title }) => `Reading: ${title}`,

    "feedback.title": "Help us improve",
    "feedback.body": "Tell us what worked or what we could improve about this adjusted reading.",
    "feedback.comment": "Your feedback",
    "feedback.placeholder": "Share your thoughts about this reading",
    "feedback.count": ({ count }) => `${count}/2000 characters`,
    "feedback.loading": "Loading your feedback...",
    "feedback.loadError": "We couldn't load your feedback. Try again before writing a new response.",
    "feedback.retry": "Try again",
    "feedback.submit": "Submit feedback",
    "feedback.saveChanges": "Save changes",
    "feedback.saving": "Saving...",
    "feedback.required": "Enter a comment before submitting.",
    "feedback.tooLong": "Feedback must be 2,000 characters or fewer.",
    "feedback.saved": "Thanks—your feedback was saved.",
    "feedback.saveError": "We couldn't save your feedback. Your comment is still here; please try again.",

    "settings.title": "Settings",
    "settings.subtitle": "Customize your learning experience",
    "settings.languageTitle": "Display Language",
    "settings.languageBody":
      "Choose the language used for app menus, buttons, and settings.",
    "settings.targetTitle": "Target Learning Level",
    "settings.targetBody":
      "Choose your target CEFR level for article simplification.",
    "settings.debugTitle": "Reader debug mode",
    "settings.debugBody":
      "Show change highlights, GPT notes, and the requested CEFR level in Reader.",
    "settings.debugWarning":
      "Developer aid only. This does not hide debug data from network responses.",
    "settings.debugToggle": "Debug mode",
    "settings.readingPreferences": "Reading Preferences",
    "settings.account": "Account",
    "settings.displayName": "Display name",
    "settings.displayNameRequired": "Enter a display name.",
    "settings.localRefreshWarning":
      "Your account changes were saved, but some local settings could not refresh. Refresh the page.",
    "settings.email": "Email",
    "settings.notAvailable": "Not available",
    "settings.saved": "Settings saved successfully!",
    "settings.save": "Save Settings",
    "settings.saving": "Saving...",
    "settings.changePassword": "Change password",
    "settings.changePasswordBody":
      "Verify your current password and choose a new one.",
    "settings.googlePassword": "Password managed by Google",
    "settings.googlePasswordBody":
      "Manage this account's password through Google.",
    "settings.currentPassword": "Current password",
    "settings.newPassword": "New password",
    "settings.confirmPassword": "Confirm new password",
    "settings.passwordLength": "Password must be at least 6 characters long.",
    "settings.passwordMismatch": "Passwords do not match.",
    "settings.incorrectCurrentPassword": "Your current password is incorrect.",
    "settings.passwordChanged": "Password changed successfully.",
    "settings.passwordChangedCleanupWarning":
      "Your password changed, but the temporary verification session could not be cleared. Refresh this page before changing your password again.",
    "settings.changingPassword": "Changing...",
    "settings.changePasswordError":
      "Could not change your password. Please try again.",
    "settings.passwordStatusError": "Could not load password settings.",
    "settings.deleteAccount": "Delete Account",
    "settings.deleteAccountBody":
      "Permanently delete your account and all data",
    "settings.deleteDialogBody":
      "This permanently deletes your account, learning history, vocabulary, uploaded readings, and settings.",
    "settings.typeDelete": "Type DELETE to confirm",
    "settings.cancel": "Cancel",
    "settings.deleting": "Deleting...",

    "history.title": "Reading History",
    "history.subtitle": ({ count }) =>
      `Your completed imported readings (${count} total)`,
    "history.loading": "Loading reading history...",
    "history.loadError": "Failed to load reading history",
    "history.unexpectedError":
      "An unexpected error occurred while loading history",
    "history.readOn": ({ date }) => `Read on ${date}`,
    "history.yesterday": "Yesterday",
    "history.daysAgo": ({ count }) => `${count} days ago`,
    "history.weeksAgo": ({ count }) =>
      `${count} week${Number(count) > 1 ? "s" : ""} ago`,
    "history.emptyTitle": "No reading history yet",
    "history.emptyBody": "Complete a Library reading to build your history",
    "history.openAgain": "Click any row to open it again",

    "vocab.title": "Saved Vocabulary",
    "vocab.subtitle": ({ count }) => `Your learning words (${count} total)`,
    "vocab.loading": "Loading vocabulary...",
    "vocab.loadError": "Failed to load saved vocabulary",
    "vocab.unexpectedError":
      "An unexpected error occurred while loading vocabulary",
    "vocab.lookupCount": ({ count }) =>
      `${count} lookup${Number(count) !== 1 ? "s" : ""}`,
    "vocab.remove": "Remove",
    "vocab.removeTitle": "Remove from saved vocabulary",
    "vocab.loadingDefinition": "Loading definition...",
    "vocab.emptyTitle": "No vocabulary saved yet",
    "vocab.emptyBody": "Open a Library reading to look up and save words",
    "vocab.definitionUnavailable": "Definition not available",
    "vocab.removeError": "Failed to remove word",
    "vocab.matched": ({ word }) => `matched ${word}`,
    "vocab.similar": "Similar:",
    "vocab.opposite": "Opposite:",
    "vocab.viewSource": "View source",
    "vocab.playPronunciation": "Play pronunciation",

    "onboarding.errorNoProfile": "No user profile found. Please start over.",
    "onboarding.errorChooseLevel":
      "Choose your current reading level to continue.",
    "onboarding.errorUpdate": ({ message }) =>
      `Error updating user profile: ${message}`,
    "onboarding.errorSetup": ({ message }) =>
      `Error setting up user profile: ${message}`,
    "onboarding.welcome": ({ name }) => `Welcome, ${name}!`,
    "onboarding.user": "User",
    "onboarding.subtitle":
      "Choose the level that best matches what you can comfortably read today. These test-score ranges are approximate references, not official conversions.",
    "onboarding.changeLater": "You can change this later in Settings.",
    "onboarding.settingUp": "Setting up...",
    "onboarding.startReading": "Start Reading",
    "onboarding.setupError": "Setup Error",
    "onboarding.backToLogin": "Back to Login",
    "level.A1.name": "Beginner",
    "level.A1.description":
      "I can understand familiar everyday phrases and very simple sentences.",
    "level.A2.name": "Elementary",
    "level.A2.description":
      "I can follow short, clear texts about common daily topics.",
    "level.B1.name": "Intermediate",
    "level.B1.description":
      "I can read straightforward articles and understand the main points.",
    "level.B2.name": "Upper Intermediate",
    "level.B2.description":
      "I can understand detailed articles and opinions on many topics.",
    "level.C1.name": "Advanced",
    "level.C1.description":
      "I can handle long, complex texts and infer meaning from style and context.",
    "level.C2.name": "Proficient",
    "level.C2.description":
      "I can read demanding texts with subtle language and specialized vocabulary.",
  },
  zh: {
    "common.appName": "PageBunny",
    "common.loading": "加载中...",
    "common.tryAgain": "重试",
    "common.refresh": "刷新",
    "common.delete": "删除",
    "common.completed": "已完成",
    "common.sections": "章节",
    "common.sectionCount": ({ count }) => `${count} 个章节`,
    "common.words": ({ count }) => `${count} 个单词`,
    "common.min": ({ count }) => `${count} 分钟`,
    "common.untitledReading": "未命名阅读",
    "common.userReading": "用户阅读",
    "common.article": "文章",
    "common.importedReading": "导入阅读",
    "common.language": "显示语言",
    "common.english": "English",
    "common.chinese": "中文",

    "nav.library": "书库",
    "nav.savedVocab": "已存词汇",
    "nav.history": "历史",
    "nav.settings": "设置",
    "nav.openMenu": "打开导航菜单",
    "nav.mobileLabel": "移动端导航",
    "nav.welcome": ({ name }) => `欢迎，${name}!`,
    "nav.logout": "退出登录",

    "login.startReading": "开始阅读",
    "login.seeHowItWorks": "了解使用方式",
    "login.howItWorksTitle": "使用方式",
    "login.howItWorksBody":
      "带上你想读的英文内容，把它变成适合自己水平的阅读练习。",
    "login.workflow.levelTitle": "选择你的水平",
    "login.workflow.levelBody": "选择适合你的 CEFR 阅读水平。",
    "login.workflow.addTitle": "添加阅读内容",
    "login.workflow.addBody": "粘贴文本，或从设备上传 TXT/EPUB 文件。",
    "login.workflow.readTitle": "阅读并学习词汇",
    "login.workflow.readBody": "阅读适配后的版本，查词并保存有用词汇。",
    "login.workflow.pasteLabel": "粘贴文本",
    "login.workflow.definitionLabel": "释义",
    "login.workflow.saveLabel": "保存词汇",
    "login.heroTitle": "阅读你真正关心的英文内容",
    "login.heroBody":
      "粘贴文本或上传 TXT/EPUB 文件。PageBunny 会按你的 CEFR 水平改写内容，帮你查词，并保存词汇用于复习。",
    "login.privacyNote": "你导入的阅读内容仅对你的账号可见。",
    "login.exploreLabel": "了解 PageBunny",
    "login.exploreTextSimplifier": "英文难度调整",
    "login.exploreEpubReader": "EPUB 分级阅读",
    "login.exploreChineseLearners": "中文学习者入口",
    "login.loginTitle": "登录",
    "login.loginSubtitle": "打开你的私人阅读书库。",
    "login.email": "邮箱地址",
    "login.emailPlaceholder": "输入邮箱",
    "login.password": "密码",
    "login.passwordPlaceholder": "输入密码",
    "login.loginButton": "登录",
    "login.loggingIn": "正在登录...",
    "login.orContinue": "或继续使用",
    "login.signingIn": "正在登录...",
    "login.google": "使用 Google 继续",
    "login.noAccount": "还没有账号？",
    "login.signupLink": "注册",
    "login.signupTitle": "创建账号",
    "login.signupSubtitle": "保存阅读、词汇和学习进度。",
    "login.name": "你的姓名",
    "login.namePlaceholder": "输入姓名",
    "login.createAccount": "创建账号",
    "login.creatingAccount": "正在创建账号...",
    "login.hasAccount": "已有账号？",
    "login.backToLogin": "返回登录",
    "login.continueToApp": "继续使用应用",
    "login.forgotPassword": "忘记密码？",
    "login.forgotTitle": "重置密码",
    "login.forgotSubtitle": "输入邮箱，我们会发送安全的密码重置链接。",
    "login.sendResetLink": "发送重置链接",
    "login.sendingResetLink": "正在发送...",
    "login.resetRequested":
      "如果该邮箱对应一个账号，我们已发送密码重置链接。请检查收件箱和垃圾邮件。如果没有收到，请确认邮箱地址或创建新账号。",
    "login.resetRequestError": "无法发送重置邮件，请重试。",
    "login.passwordUpdated": "密码已更新。请使用新密码登录。",
    "login.errorEmailPassword": "请输入邮箱和密码",
    "login.errorAllFields": "请填写所有字段",
    "login.errorPasswordLength": "密码长度至少为 6 个字符",
    "login.errorUnexpected": "发生未知错误",
    "login.errorGoogle": "Google 登录失败",
    "login.errorConfirmEmail":
      "请查看邮箱完成账号创建。如果你已用此邮箱注册，请登录或重置密码。",

    "reset.loading": "正在验证重置链接...",
    "reset.title": "设置新密码",
    "reset.subtitle": "请使用至少 6 个字符。",
    "reset.newPassword": "新密码",
    "reset.confirmPassword": "确认新密码",
    "reset.update": "更新密码",
    "reset.updating": "正在更新...",
    "reset.invalidTitle": "重置链接不可用",
    "reset.invalidBody": "此密码重置链接无效或已过期。",
    "reset.requestAnother": "重新申请重置链接",
    "reset.googleTitle": "密码由 Google 管理",
    "reset.googleBody": "请通过 Google 管理此账号的密码。",
    "reset.passwordLength": "密码长度至少为 6 个字符。",
    "reset.passwordMismatch": "两次输入的密码不一致。",
    "reset.updateError": "无法更新密码，请重试。",
    "reset.cleanupTitle": "无法清除密码恢复授权",
    "reset.cleanupBody": "无法安全清除此重置会话。请重试清理后再申请新链接。",
    "reset.retryCleanup": "重试安全清理",

    "library.title": "书库",
    "library.subtitle": "你导入的私人阅读，用于 CEFR 适配练习。",
    "library.loadError": "无法加载你的书库",
    "library.loading": "正在加载书库...",
    "library.emptyTitle": "从你想读的内容开始",
    "library.emptyBody":
      "粘贴文章、论文、新闻简报、课堂阅读，或上传 TXT/EPUB 文件。PageBunny 会按你的 CEFR 水平调整内容，并帮助你保存词汇。",
    "library.privateNote": "导入的阅读内容仅对你的账号可见。",
    "library.uploadCta": "上传 TXT/EPUB",
    "library.pasteCta": "粘贴文本",
    "library.hideSections": "收起章节",
    "library.lastOpened": ({ date }) => `上次打开：${date}`,
    "library.notOpened": "尚未打开",
    "library.sourcePasted": "粘贴文本",
    "library.sourceTxt": "TXT 上传",
    "library.sourceEpub": "EPUB 上传",
    "library.sourceExtension": "扩展导入",
    "library.importReady": "导入完成",
    "library.simplifiedProgress": ({ count, total }) =>
      `${count}/${total} 已简化`,
    "library.uploadTitle": "上传 TXT/EPUB",
    "library.uploadBody": "添加纯文本文件或 EPUB 到你的私人书库。",
    "library.titleOptional": "标题（可选）",
    "library.dropFile": "将 TXT 或 EPUB 文件拖到这里",
    "library.chooseFileLine": "或从设备中选择文件",
    "library.chooseFile": "选择文件",
    "library.uploadLimits": "TXT 不超过 1 MB，EPUB 不超过 10 MB。",
    "library.uploading": "正在上传...",
    "library.uploadFile": "上传文件",
    "library.pasteTitle": "粘贴文本",
    "library.pasteBody": "从文章、论文、新闻简报或课堂阅读中导入文本。",
    "library.titlePlaceholder": "标题",
    "library.sourceUrl": "来源 URL（可选）",
    "library.pastePlaceholder": "粘贴你想阅读的文本",
    "library.importing": "正在导入...",
    "library.importPasted": "导入粘贴文本",
    "library.retryPaste": "重试导入粘贴文本",
    "library.retryUpload": "重试导入文件",
    "library.checkImportAgain": "再次检查导入",
    "library.importStillProcessing": "这篇阅读仍在处理中。",
    "library.selectSameFile": "请选择同一文件以重试",
    "library.adjustFailure": "无法调整这篇阅读",
    "library.retrySimplification": "重试简化",
    "library.checkSimplificationAgain": "再次检查",
    "library.stillSimplifying": "仍在简化中",
    "library.simplifying": "正在简化...",
    "library.read": "阅读",
    "library.simplifyRead": "简化并阅读",
    "library.creatingAdjusted": "正在创建适配版本...",
    "library.deleteConfirm": ({ title }) => `删除“${title}”？`,
    "library.backToLibrary": "返回书库",
    "library.readingUnavailable": "此阅读不可用。",
    "library.readerHeading": ({ title }) => `阅读：${title}`,

    "feedback.title": "帮助我们改进",
    "feedback.body": "请告诉我们这篇调整后的阅读哪些地方做得好，或哪些地方可以改进。",
    "feedback.comment": "你的反馈",
    "feedback.placeholder": "分享你对这篇阅读的想法",
    "feedback.count": ({ count }) => `${count}/2000 个字符`,
    "feedback.loading": "正在加载你的反馈...",
    "feedback.loadError": "无法加载你的反馈。请先重试，再填写新的回复。",
    "feedback.retry": "重试",
    "feedback.submit": "提交反馈",
    "feedback.saveChanges": "保存更改",
    "feedback.saving": "正在保存...",
    "feedback.required": "请先输入反馈内容。",
    "feedback.tooLong": "反馈不能超过 2,000 个字符。",
    "feedback.saved": "谢谢，你的反馈已保存。",
    "feedback.saveError": "无法保存反馈。你的内容仍保留在这里，请重试。",

    "settings.title": "设置",
    "settings.subtitle": "自定义你的学习体验",
    "settings.languageTitle": "显示语言",
    "settings.languageBody": "选择应用菜单、按钮和设置中使用的语言。",
    "settings.targetTitle": "目标学习水平",
    "settings.targetBody": "选择文章简化时使用的目标 CEFR 水平。",
    "settings.debugTitle": "阅读器调试模式",
    "settings.debugBody": "在阅读器中显示文本变化、GPT 备注和目标 CEFR 等级。",
    "settings.debugWarning": "仅供开发调试；网络响应中的调试数据不会因此隐藏。",
    "settings.debugToggle": "调试模式",
    "settings.readingPreferences": "阅读偏好",
    "settings.account": "账号",
    "settings.displayName": "显示名称",
    "settings.displayNameRequired": "请输入显示名称。",
    "settings.localRefreshWarning":
      "账号更改已保存，但部分本地设置无法刷新。请刷新页面。",
    "settings.email": "电子邮箱",
    "settings.notAvailable": "不可用",
    "settings.saved": "设置已保存！",
    "settings.save": "保存设置",
    "settings.saving": "正在保存...",
    "settings.changePassword": "更改密码",
    "settings.changePasswordBody": "验证当前密码并设置新密码。",
    "settings.googlePassword": "密码由 Google 管理",
    "settings.googlePasswordBody": "请通过 Google 管理此账号的密码。",
    "settings.currentPassword": "当前密码",
    "settings.newPassword": "新密码",
    "settings.confirmPassword": "确认新密码",
    "settings.passwordLength": "密码长度至少为 6 个字符。",
    "settings.passwordMismatch": "两次输入的密码不一致。",
    "settings.incorrectCurrentPassword": "当前密码不正确。",
    "settings.passwordChanged": "密码更改成功。",
    "settings.passwordChangedCleanupWarning":
      "密码已更改，但临时验证会话未能安全清除。再次更改密码前，请刷新此页面。",
    "settings.changingPassword": "正在更改...",
    "settings.changePasswordError": "无法更改密码，请重试。",
    "settings.passwordStatusError": "无法加载密码设置。",
    "settings.deleteAccount": "删除账号",
    "settings.deleteAccountBody": "永久删除你的账号和所有数据",
    "settings.deleteDialogBody":
      "这会永久删除你的账号、学习历史、词汇、上传阅读和设置。",
    "settings.typeDelete": "输入 DELETE 以确认",
    "settings.cancel": "取消",
    "settings.deleting": "正在删除...",

    "history.title": "阅读历史",
    "history.subtitle": ({ count }) => `你完成的导入阅读（共 ${count} 条）`,
    "history.loading": "正在加载阅读历史...",
    "history.loadError": "无法加载阅读历史",
    "history.unexpectedError": "加载阅读历史时发生未知错误",
    "history.readOn": ({ date }) => `阅读时间：${date}`,
    "history.yesterday": "昨天",
    "history.daysAgo": ({ count }) => `${count} 天前`,
    "history.weeksAgo": ({ count }) => `${count} 周前`,
    "history.emptyTitle": "还没有阅读历史",
    "history.emptyBody": "完成书库中的阅读后，这里会显示记录",
    "history.openAgain": "点击任意行即可再次打开",

    "vocab.title": "已存词汇",
    "vocab.subtitle": ({ count }) => `你的学习词汇（共 ${count} 个）`,
    "vocab.loading": "正在加载词汇...",
    "vocab.loadError": "无法加载已存词汇",
    "vocab.unexpectedError": "加载词汇时发生未知错误",
    "vocab.lookupCount": ({ count }) => `${count} 次查询`,
    "vocab.remove": "移除",
    "vocab.removeTitle": "从已存词汇中移除",
    "vocab.loadingDefinition": "正在加载释义...",
    "vocab.emptyTitle": "还没有保存词汇",
    "vocab.emptyBody": "打开书库阅读内容即可查询并保存词汇",
    "vocab.definitionUnavailable": "暂无释义",
    "vocab.removeError": "移除单词失败",
    "vocab.matched": ({ word }) => `匹配 ${word}`,
    "vocab.similar": "近义词：",
    "vocab.opposite": "反义词：",
    "vocab.viewSource": "查看来源",
    "vocab.playPronunciation": "播放发音",

    "onboarding.errorNoProfile": "未找到用户资料。请重新开始。",
    "onboarding.errorChooseLevel": "请选择你当前的阅读水平以继续。",
    "onboarding.errorUpdate": ({ message }) => `更新用户资料出错：${message}`,
    "onboarding.errorSetup": ({ message }) => `设置用户资料出错：${message}`,
    "onboarding.welcome": ({ name }) => `欢迎，${name}!`,
    "onboarding.user": "用户",
    "onboarding.subtitle":
      "选择最符合你当前舒适阅读能力的水平。考试分数范围仅作近似参考，不是官方换算。",
    "onboarding.changeLater": "之后可以在设置中更改。",
    "onboarding.settingUp": "正在设置...",
    "onboarding.startReading": "开始阅读",
    "onboarding.setupError": "设置错误",
    "onboarding.backToLogin": "返回登录",
    "level.A1.name": "入门",
    "level.A1.description": "我能理解熟悉的日常短语和非常简单的句子。",
    "level.A2.name": "基础",
    "level.A2.description": "我能读懂关于常见日常主题的简短清晰文本。",
    "level.B1.name": "中级",
    "level.B1.description": "我能阅读直接明了的文章并理解主要内容。",
    "level.B2.name": "中高级",
    "level.B2.description": "我能理解许多主题的详细文章和观点。",
    "level.C1.name": "高级",
    "level.C1.description":
      "我能处理长而复杂的文本，并从风格和语境中推断含义。",
    "level.C2.name": "精通",
    "level.C2.description": "我能阅读语言细腻、词汇专业的高难度文本。",
  },
} satisfies Record<DisplayLanguage, Record<string, TranslationValue>>;

export type TranslationKey = keyof typeof translations.en;

interface LanguageContextValue {
  language: DisplayLanguage;
  setLanguage: (language: DisplayLanguage) => void;
  t: (key: TranslationKey, values?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

const getInitialLanguage = (): DisplayLanguage => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "en" || stored === "zh") return stored;
  return navigator.language.toLowerCase().startsWith("zh") ? "zh" : "en";
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [language, setLanguageState] =
    useState<DisplayLanguage>(getInitialLanguage);

  useEffect(() => {
    document.documentElement.lang = language === "zh" ? "zh-CN" : "en";
    localStorage.setItem(STORAGE_KEY, language);
  }, [language]);

  const value = useMemo<LanguageContextValue>(() => {
    const setLanguage = (nextLanguage: DisplayLanguage) => {
      setLanguageState(nextLanguage);
    };

    const t = (
      key: TranslationKey,
      values: Record<string, string | number> = {},
    ) => {
      const entry = translations[language][key] ?? translations.en[key];
      return typeof entry === "function" ? entry(values) : entry;
    };

    return { language, setLanguage, t };
  }, [language]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};
